import { isSameDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { type CSSProperties, useCallback, useMemo } from 'react';
import { TIMEZONE_COLUMN_WIDTH } from '../../constants';
import type { Timezones } from '../../types';

type Properties = {
  days: Date[];
  isWeekMode: boolean;
  primaryTimezone: Timezones[number];
  timezoneCount: number;
  top: number;
};

export const useGetNowLineStyle = ({ days, isWeekMode, primaryTimezone, timezoneCount, top }: Properties) => {
  const todayInPrimaryTimezone = toZonedTime(new Date(), primaryTimezone);

  const currentDayIndex = useMemo(
    () => days.findIndex((day) => isSameDay(day, todayInPrimaryTimezone)),
    [days, todayInPrimaryTimezone],
  );

  const nowDotStyle: CSSProperties = useMemo(
    () =>
      isWeekMode && currentDayIndex !== -1 ? { left: `calc(${(100 / days.length) * currentDayIndex}% - 3.5px)` } : {},
    [days.length, currentDayIndex, isWeekMode],
  );

  const nowLineStyle: CSSProperties = useMemo(
    () => ({
      left: `${TIMEZONE_COLUMN_WIDTH * timezoneCount}px`,
      top,
    }),
    [timezoneCount, top],
  );

  const isCurrentDay = useCallback((day: Date) => isSameDay(day, todayInPrimaryTimezone), [todayInPrimaryTimezone]);

  return useMemo(
    () => ({
      isCurrentDay,
      nowDotStyle,
      nowLineStyle,
    }),
    [isCurrentDay, nowDotStyle, nowLineStyle],
  );
};
